'use client';

import { useCallback, useLayoutEffect, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import {
  readVisibleScrollAnchor,
  scrollTopForAnchor,
  type ChatScrollAnchor,
  type ChatScrollRow,
} from './chat-scroll-anchor';
import {
  readChatScrollPosition,
  rememberChatScrollPosition,
} from './chat-scroll-memory';
import type { ChatScrollFollowState } from './chat-scroll-follow';

export function useChatScrollSession({
  rows,
  scrollElementRef,
  isScrollNearBottom,
  followStateRef,
  isInitialScrollPendingRef,
  scrollSessionId,
  isScrollerActive,
  scrollToLastItem,
}: {
  rows: readonly ChatScrollRow[];
  scrollElementRef: RefObject<HTMLDivElement | null>;
  isScrollNearBottom: () => boolean;
  followStateRef: MutableRefObject<ChatScrollFollowState>;
  isInitialScrollPendingRef: MutableRefObject<boolean>;
  scrollSessionId?: string;
  isScrollerActive: boolean;
  scrollToLastItem: () => void;
}): {
  anchorRef: MutableRefObject<ChatScrollAnchor | null>;
  rememberPosition: (scrollTop: number) => void;
  restoreSavedPosition: (scrollElement: HTMLDivElement) => boolean;
} {
  const anchorRef = useRef<ChatScrollAnchor | null>(null);
  const scrollSessionIdRef = useRef(scrollSessionId);
  const isScrollerActiveRef = useRef(isScrollerActive);
  const previousScrollerActiveRef = useRef(isScrollerActive);

  scrollSessionIdRef.current = scrollSessionId;
  isScrollerActiveRef.current = isScrollerActive;

  const rememberPosition = useCallback(
    (scrollTop: number) => {
      const anchor = readVisibleScrollAnchor({
        scrollTop,
        rows,
      });

      anchorRef.current = anchor;

      const sessionId = scrollSessionIdRef.current;

      if (!sessionId || !isScrollerActiveRef.current) {
        return;
      }

      if (isScrollNearBottom()) {
        rememberChatScrollPosition({
          sessionId,
          position: { kind: 'bottom' },
        });
        return;
      }

      if (!anchor) {
        return;
      }

      rememberChatScrollPosition({
        sessionId,
        position: { kind: 'anchor', ...anchor },
      });
    },
    [isScrollNearBottom, rows],
  );

  const restoreSavedPosition = useCallback(
    (scrollElement: HTMLDivElement) => {
      const sessionId = scrollSessionIdRef.current;

      if (!sessionId) {
        return false;
      }

      const saved = readChatScrollPosition(sessionId);

      if (!saved || saved.kind === 'bottom') {
        return false;
      }

      const nextTop = scrollTopForAnchor({
        anchor: saved,
        rows,
      });

      if (nextTop === null) {
        return false;
      }

      followStateRef.current = {
        isUserNearBottom: false,
        shouldKeepFollowingBottom: false,
      };
      isInitialScrollPendingRef.current = false;
      scrollElement.scrollTop = nextTop;
      anchorRef.current = saved;
      return true;
    },
    [followStateRef, isInitialScrollPendingRef, rows],
  );

  useLayoutEffect(() => {
    const becameActive = !previousScrollerActiveRef.current && isScrollerActive;

    previousScrollerActiveRef.current = isScrollerActive;

    if (!becameActive) {
      return;
    }

    const scrollElement = scrollElementRef.current;

    if (!scrollElement) {
      return;
    }

    if (restoreSavedPosition(scrollElement)) {
      return;
    }

    const saved = scrollSessionId
      ? readChatScrollPosition(scrollSessionId)
      : undefined;

    if (saved?.kind === 'bottom') {
      scrollToLastItem();
    }
  }, [
    isScrollerActive,
    restoreSavedPosition,
    scrollElementRef,
    scrollSessionId,
    scrollToLastItem,
  ]);

  return { anchorRef, rememberPosition, restoreSavedPosition };
}
